/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Component, CSSProperties, useEffect, useState } from 'react';
import { css } from 'emotion';
import Dropzone from 'react-dropzone';
import immer from 'immer';
import colors from '../../../../css/colors';
import Button from '../../../ui/Button';
import FilesList from '../../../orders/order-information/FilesList';
import getBase64 from '../../../FileUploads';
import axios from 'axios';
import styled from '@emotion/styled';
import Select from 'react-select';
import Spinner from '../../../ui/Spinner';
import { SettingName, SettingOption } from '../../../../types';

const dragBoxStyles: CSSProperties = {
  alignItems: 'center',
  color: 'black',
  display: 'flex',
  flexDirection: 'column',
  fontWeight: 'bold',
  justifyContent: 'center',
  marginTop: 32,
  padding: '24px 16px',
  textAlign: 'center',
  width: '100%',
  height: '100%',
  cursor: 'pointer',
};

const cancelText: CSSProperties = {
  textDecorationLine: 'underline',
  fontSize: '14px',
  padding: '16px',
  cursor: 'pointer',
};

const buttonStyles: CSSProperties = {
  backgroundColor: colors.blackText,
  color: colors.white,
  marginTop: 16,
};

const customStyles = {
  container: (provided: any, state: any) => ({
    ...provided,
    width: 296,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    boxShadow: state.isFocused && '1px solid #ff5252',
    border: state.isFocused ? '1px solid #ff5252' : `1px solid ${colors.grey}`,
    height: 48,
    '&:hover': {
      border: state.isFocused && '1px solid #ff5252',
    },
  }),
};

interface Proptypes {
  searchId: number;
  setIsAddingFile: (bool: boolean) => void;
  getDocs: () => void;
  documentTypes: any[];
  names?: any[];
  searchTypeId?: number;
}

const nameSearchIds = [35, 36, 38, 90];
const AddDocumentFromDocStation = (props: Proptypes) => {
  const [disableBtn, setDisableBtn] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [specialNameSearchClient, setSpecialNameSearchClient] = useState(false);
  const [frontMessageId, setFrontMessageId] = useState('');

  const onDrop = async (files: any) => {
    if (specialNameSearchClient && !selectedName) {
      alert('Please select a name before uploading files for this company.');
      return;
    }
    setDisableBtn(true);
    let filesArray: any[] = [];
    files.forEach((file: any) => {
      filesArray.push({
        file,
        customFileName: selectedName?.label ? `${selectedName?.label}.pdf` : file.name,
        actualFileName: selectedName?.label ? `${selectedName?.label}.pdf` : file.name,
      });
    });
    await onUploadClick(filesArray);
  };

  useEffect(() => {
    if (nameSearchIds.includes(props.searchTypeId)) {
      axios
        .get(
          `/api/clients/GetClientSettingBySearchId/${props.searchId}?setting=${SettingName.namesearchsepfile}`
        )
        .then(({ data }) => {
          setSpecialNameSearchClient(data === SettingOption.yes);
        });
    }
    if (props.documentTypes && props.documentTypes.length === 1) {
      setDocumentType(props.documentTypes[0]);
    }
  }, [props.searchId]);

  const onUploadClick = async (files: any) => {
    setDisableBtn(true);
    let newFiles: any[] = [];
    if (files.length) {
      const promises: any = files.map(async (f: any) => {
        const base64 = await getBase64(f.file);
        return { ...f, file: base64 };
      });

      newFiles = await Promise.all(promises);
    }
    const obj = {
      documentTypeId: documentType?.value || 3,
      searchIds: [props.searchId],
      files: newFiles,
      fileName: selectedName?.label ? `${selectedName?.label}.pdf` : undefined,
      frontMessageId,
    };
    await axios.post('/api/orders/uploadSearchDocument', obj);
    setDisableBtn(false);
    props.getDocs();
    setDisableBtn(false);
    props.setIsAddingFile(false);
  };

  const getDragActiveText = (isDragActive: boolean) => {
    if (isDragActive) {
      return <p>Drag and drop to import file</p>;
    }

    return (
      <div>
        <p>
          Drag and drop a file here,
          <br />
          or click to upload.
        </p>
      </div>
    );
  };

  return (
    <div>
      <div style={{ padding: 8, display: 'flex', justifyContent: 'space-between' }}>
        <Select
          name="DocumentType"
          value={documentType}
          options={props.documentTypes}
          onChange={dt => setDocumentType(dt)}
          styles={customStyles}
          placeholder="Select Document Type"
        />
        {specialNameSearchClient && (
          <Select
            name="Name"
            value={selectedName}
            options={props.names.map(name => ({ value: name.id, label: name.name }))}
            onChange={dt => setSelectedName(dt)}
            styles={customStyles}
            placeholder="Select Name"
          />
        )}
      </div>
      <div style={{ marginBottom: 16, padding: 8 }}>
        <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Front Message ID</label>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <input
            type="text"
            value={frontMessageId}
            onChange={e => setFrontMessageId(e.target.value)}
            placeholder="Enter Front Message ID"
            style={{
              padding: '8px 12px',
              border: '1px solid #e5e5ea',
              borderRadius: 4,
              width: 376,
              fontSize: 14,
            }}
          />
          <button
            type="button"
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
            onClick={onUploadClick}
            disabled={!frontMessageId}
          >
            Add
          </button>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginLeft: '40px',
          marginRight: '40px',
          marginBottom: '16px',
          flexDirection: 'column',
          border: `1px dashed ${colors.grey}`,
          textAlign: 'center',
          borderRadius: 4,
        }}
        onClick={(e: any) => {
          e.stopPropagation();
        }}
      >
        <Dropzone onDrop={onDrop} multiple={true}>
          {({ getRootProps, getInputProps, isDragActive }) => {
            return (
              <div style={dragBoxStyles} {...getRootProps()}>
                <input {...getInputProps()} />
                {getDragActiveText(isDragActive)}
                <Button type="button" disabled={disableBtn} baseStyles overRidestyle={buttonStyles}>
                  Select file
                </Button>
              </div>
            );
          }}
        </Dropzone>
        <span
          onClick={(e: any) => {
            e.stopPropagation();
            props.setIsAddingFile(false);
          }}
          style={cancelText}
        >
          Cancel
        </span>
        {disableBtn && <Spinner />}
      </div>
    </div>
  );
};
export default AddDocumentFromDocStation;
